"use client"

import { api } from "@/lib/api-client"
import { useApiMutation } from "@/shared/hooks/use-api-mutation"
import { useQueryClient } from "@tanstack/react-query"
import { notificationsQueryKeys } from "./use-notifications-queries"

type CreateNotificationData = {
    title: string
    message?: string
    type?: "info" | "success" | "warning" | "error" | "system"
    important?: boolean
    read?: boolean
    source?: "system" | "payment" | "transaction"
    metadata?: Record<string, unknown>
}

export function useNotificationMutations() {
    const queryClient = useQueryClient()

    const invalidate = () => {
        queryClient.invalidateQueries({ queryKey: notificationsQueryKeys.all })
    }

    const { mutate: addNotification } = useApiMutation(
        async (data: CreateNotificationData) => {
            const res = await api.notifications.$post({
                json: {
                    title: data.title,
                    message: data.message,
                    type: data.type || "info",
                    important: data.important || false,
                    source: data.source || "system",
                    metadata: data.metadata,
                },
            })
            return await res.json()
        },
        {
            showSuccessToast: true,
            successMessage: "Notification sent",
            onSuccess: invalidate,
        }
    )

    const { mutate: markRead } = useApiMutation(
        async (id: string) => {
            const res = await api.notifications[":id"].read.$patch({
                param: { id },
            })
            return await res.json()
        },
        {
            onSuccess: invalidate,
        }
    )

    const { mutate: markAllRead } = useApiMutation(
        async () => {
            const res = await api.notifications["read-all"].$patch()
            return await res.json()
        },
        {
            showSuccessToast: true,
            successMessage: "All notifications marked as read",
            onSuccess: invalidate,
        }
    )

    const { mutate: removeNotification } = useApiMutation(
        async (id: string) => {
            const res = await api.notifications[":id"].$delete({
                param: { id },
            })
            return await res.json()
        },
        {
            showSuccessToast: true,
            successMessage: "Notification deleted",
            onSuccess: invalidate,
        }
    )

    const { mutate: toggleImportant } = useApiMutation(
        async (id: string) => {
            const res = await api.notifications[":id"].important.$patch({
                param: { id },
            })
            return await res.json()
        },
        {
            onSuccess: invalidate,
        }
    )

    const { mutate: updateNotification } = useApiMutation(
        async ({ id, data }: { id: string; data: Partial<CreateNotificationData> }) => {
            const res = await api.notifications[":id"].$patch({
                param: { id },
                json: data,
            })
            return await res.json()
        },
        {
            showSuccessToast: true,
            successMessage: "Notification updated",
            onSuccess: invalidate,
        }
    )

    return {
        addNotification,
        markRead,
        markAllRead,
        removeNotification,
        toggleImportant,
        updateNotification,
    }
}
